import { removeNulls } from './common/TypeScriptUtility';
import { RoseTree } from './RoseTree';

/**
 * Returns a copy of the tree where any node that does not satisfy the predicate has been omitted along with all of its descendants,
 * regardless of whether or not the descendants satisfy the predicate. In other words, the remaining nodes are those whose entire ancestry
 * chain (up to the root) satisfies the predicate. The root node is always kept, whether or not it satisfies the predicate.
 *
 * @param tree - The tree to filter.
 * @param p - The predicate.
 */
export function filterSubtreeRoots<T>(tree: RoseTree<T>, p: (t: T) => boolean): RoseTree<T> {
  let children = tree.children.filter(child => p(child.node)).map(child => filterSubtreeRoots(child, p));
  return new RoseTree(tree.node, children);
}

/**
 * Returns a copy of the tree in which subtrees have been omitted iff all the elements in the subtree (including its root) do not satisfy
 * the predicate. In other words, all the items anywhere in the tree that satisfied the predicate remain in the result, along with the nodes
 * that connect them to the root.
 *
 * @param tree - The tree to filter.
 * @param p - The predicate.
 */
export function filterSubtrees<T>(tree: RoseTree<T>, p: (t: T) => boolean): RoseTree<T> | null {
  let children = removeNulls(tree.children.map(child => filterSubtrees(child, p)));
  return p(tree.node) ? new RoseTree(tree.node, children) : null;
}
